import {
    getPlacementOptions,
    getAccountOptions,
    getPlatformOptions,
} from '../filters/getSpecificOptions';
import getOptions from '../../utils/getFilterOptions';

export const getIdsOptions = ({ filters, client, type = 'collections' }) => {
    const {
        rightsholders,
        sponsors,
        placements,
        accOwnTypes,
        mediaTypes,
        platformPostTypePairs,
        hasSeveralRightsholders,
    } = filters;

    const userPermissions = client.permissions || [];

    const rightsholderPlacementIdsOptions = getPlacementOptions({
        plcList: placements.list,
        rhList: rightsholders.list,
        hasSeveralRightsholders,
    });

    const rhOptions = getOptions({ list: rightsholders.list });
    const spOptions = getOptions({ list: sponsors.list });

    const accountTypesOptions = getAccountOptions({
        list: accOwnTypes.list,
        userPermissions,
    });

    const platformsOptions = getPlatformOptions({
        list: platformPostTypePairs.list,
        nested: true,
        includeBroadcast: type !== 'campaigns',
    });

    const socialHandlePlatformsOptions = getPlatformOptions({
        list: platformPostTypePairs.list,
        nested: false,
        includeName: true,
        includeBroadcast: false,
    });

    const mediaOptionsOptions = getOptions({ list: mediaTypes.list });

    const config = {
        rightsholders: rhOptions,
        rightsholderPlacements: rightsholderPlacementIdsOptions,
        sponsors: spOptions,
        platforms: platformsOptions,
        accountTypes: accountTypesOptions,
        mediaTypes: mediaOptionsOptions,
        accountUsernames: { platforms: socialHandlePlatformsOptions },
    };

    return config;
};

export const getParamsOptions = type => {
    const options = paramsOptions.filter(option =>
        option.entities.includes(type),
    );

    return options;
};

// SP-8141 — duplicate conditions of the same parameter.
//
// A builder row is identified by a unique *row key*. For the first occurrence of
// a parameter the row key IS the parameter value ('sponsors'); a second/third
// condition of the same parameter gets a '#N' suffix ('sponsors#2', 'sponsors#3')
// so each row keeps its own value + AND/OR operator in the flat, string-keyed
// form state. `baseParamOf` recovers the parameter value for option/type/label
// resolution and for the clean `name` sent to the backend. The backend (SP-8418)
// accepts repeated names across OR branches, so the suffix never leaves the UI.
export const ROW_KEY_SEP = '#';

export const baseParamOf = rowKey =>
    typeof rowKey === 'string' ? rowKey.split(ROW_KEY_SEP)[0] : rowKey;

/** Build a row key for `param` that doesn't collide with `existingKeys`. */
export const makeRowKey = (param, existingKeys = []) => {
    if (!existingKeys.includes(param)) return param;
    let n = 2;
    while (existingKeys.includes(`${param}${ROW_KEY_SEP}${n}`)) n++;
    return `${param}${ROW_KEY_SEP}${n}`;
};

export const paramsOptions = [
    {
        value: 'date',
        name: 'Date',
        type: 'dateRange',
        entities: ['collections', 'campaigns'],
    },
    {
        value: 'rightsholders',
        name: 'Rightsholders',
        type: 'id',
        entities: ['collections'],
    },
    {
        value: 'rightsholderPlacements',
        name: 'Placements',
        type: 'compoundId',
        entities: ['collections'],
    },
    {
        value: 'sponsors',
        name: 'Sponsors',
        type: 'id',
        entities: ['collections'],
    },
    {
        value: 'platforms',
        name: 'Platforms',
        type: 'compoundId',
        entities: ['collections', 'campaigns'],
    },
    {
        value: 'accountTypes',
        name: 'Account Types',
        type: 'id',
        entities: ['collections'],
    },
    {
        value: 'mediaTypes',
        name: 'Media Types',
        type: 'id',
        entities: ['collections', 'campaigns'],
    },
    {
        value: 'hashtags',
        name: 'Hashtags',
        type: 'text',
        placeholder: 'Enter Hashtags...',
        entities: ['collections', 'campaigns'],
    },
    {
        value: 'accountUsernames',
        name: 'Social/Streaming Account Handle',
        type: 'usernamesObject',
        entities: ['collections', 'campaigns'],
    },
    {
        value: 'impressions',
        name: 'Impressions or Video Views',
        type: 'numberRange',
        entities: ['collections', 'campaigns'],
    },
    {
        value: 'engagements',
        name: 'Engagements',
        type: 'numberRange',
        entities: ['collections', 'campaigns'],
    },
    {
        value: 'sponsorMediaValue',
        name: 'Sponsor Media Value',
        type: 'numberRange',
        entities: ['collections'],
    },
    {
        value: 'fullMediaValue',
        name: 'Full Media Value',
        type: 'numberRange',
        entities: ['collections'],
    },
    {
        value: 'averageMvpScore',
        name: 'MVP Score',
        type: 'floatRange',
        entities: ['collections'],
    },
    {
        value: 'postCaption',
        name: 'Caption',
        type: 'text',
        placeholder: 'Enter Captions...',
        entities: ['collections', 'campaigns'],
    },
];

export const numberOptions = [
    {
        value: 1,
        name: '>=',
        subs: ['start'],
    },
    { value: 2, name: '<=', subs: ['end'] },
    { value: 3, name: 'In between', subs: ['start', 'end'] },
];

export const dateOptions = [
    {
        value: 1,
        name: 'On or after',
        subs: ['start'],
    },
    { value: 2, name: 'On or before', subs: ['end'] },
    { value: 3, name: 'In between', subs: ['start', 'end'] },
];

export const isAfter = 1;
export const isBefore = 2;
export const isInBetween = 3;

export const isRunning = 'IN_PROGRESS';
export const isCompleted = 'COMPLETED';
export const isNotStarted = 'NOT_STARTED';
